import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import * as log from "@/lib/logger";
import { fireDesktopNotif } from "@/lib/desktopNotifs";
import { playIncomingCall } from "@/lib/soundEffects";
import { Headphones, Video, X, Phone } from "lucide-react";

/**
 * Persistent banner floating at the top of the viewport whenever someone else
 * in the org starts a huddle or call. Meant to be VERY hard to miss so users
 * know a teammate is calling for them — the previous "just a plain chat
 * message" pattern was easily overlooked.
 *
 * The bell chime uses HTMLAudioElement (see `soundEffects.js`) so it also
 * fires when the tab is backgrounded (WebAudio would be throttled/muted).
 *
 * Subscribes to WS events:
 *   - `call.started` → adds a banner if it's not our own call
 *   - `call.updated` → clears banner when the call ends
 */
export default function IncomingHuddleBanner({ subscribe, currentUserId, activeCallId, onJoin }) {
  const [pending, setPending] = useState([]); // [{ call, caller }]
  const chimedRef = useRef(new Set());

  const play = () => {
    playIncomingCall();
  };

  useEffect(() => {
    if (!subscribe) return;
    return subscribe(async (type, payload) => {
      if (type === "call.started") {
        const call = payload?.call;
        if (!call) return;
        if (call.started_by === currentUserId) return;
        if (call.id === activeCallId) return;
        // Fetch caller info (best-effort hydrate — org members endpoint).
        let caller = null;
        try {
          const { data } = await api.get("/orgs/current/members");
          const m = (data || []).find((x) => x.user_id === call.started_by);
          caller = m?.user || null;
        } catch (err) { log.debug("[huddle-banner] caller fetch failed", err); }
        setPending((prev) => {
          if (prev.some((p) => p.call.id === call.id)) return prev;
          return [...prev, { call, caller }];
        });
        if (!chimedRef.current.has(call.id)) {
          chimedRef.current.add(call.id);
          play();
          // Fire desktop notification only when the tab is unfocused;
          // helper handles the visibility/permission checks internally.
          const kindLabel = call.kind === "video" ? "video call" : "huddle";
          const contextLine = call.dm_id
            ? "wants to talk with you in DM"
            : call.channel_id
              ? "started a huddle in a channel"
              : "is calling from the Virtual Office";
          fireDesktopNotif(
            `${caller?.name || "Someone"} started a ${kindLabel}`,
            {
              body: contextLine,
              tag: `tovra-call-${call.id}`,
              onClick: async () => {
                try { await api.post(`/calls/${call.id}/join`); }
                catch (err) { log.debug("[huddle-banner] desktop join error", err); }
                setPending((prev) => prev.filter((p) => p.call.id !== call.id));
                onJoin && onJoin(call);
              },
            }
          );
        }
      } else if (type === "call.updated") {
        const call = payload?.call;
        if (!call || call.status === "active") return;
        setPending((prev) => prev.filter((p) => p.call.id !== call.id));
      }
    });
  }, [subscribe, currentUserId, activeCallId]);

  // Clear a banner once the current user is inside its call.
  useEffect(() => {
    if (!activeCallId) return;
    setPending((prev) => prev.filter((p) => p.call.id !== activeCallId));
  }, [activeCallId]);

  const dismiss = (id) => setPending((prev) => prev.filter((p) => p.call.id !== id));

  const accept = async ({ call }) => {
    try {
      await api.post(`/calls/${call.id}/join`);
    } catch (err) {
      log.debug("[huddle-banner] join call error", err);
    }
    dismiss(call.id);
    onJoin && onJoin(call);
  };

  if (pending.length === 0) return null;
  return (
    <div className="fixed left-1/2 -translate-x-1/2 top-4 z-[55] flex flex-col gap-2 pointer-events-none" data-testid="huddle-banner-stack">
      {pending.map(({ call, caller }) => (
        <HuddleCard key={call.id} call={call} caller={caller} onAccept={() => accept({ call })} onDismiss={() => dismiss(call.id)}/>
      ))}
    </div>
  );
}

function HuddleCard({ call, caller, onAccept, onDismiss }) {
  const isVideo = call.kind === "video";
  const contextLabel = useMemo(() => {
    if (call.dm_id) return "wants to talk in DM";
    if (call.channel_id) return "started a huddle in a channel";
    if (call.room?.startsWith("office-")) return "is calling from the Virtual Office";
    return "started a huddle";
  }, [call.dm_id, call.channel_id, call.room]);
  const Icon = isVideo ? Video : (call.dm_id ? Phone : Headphones);

  return (
    <div
      role="alert"
      data-testid={`huddle-banner-${call.id}`}
      className="pointer-events-auto min-w-[320px] max-w-md bg-white border-2 border-[var(--brand-accent)] rounded-2xl shadow-xl px-3 py-2.5 flex items-center gap-3 huddle-banner-pop"
    >
      {caller && <Avatar user={caller} size="md" ring/>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-zinc-900 truncate leading-tight">
          {caller?.name || "Someone"}
        </div>
        <div className="text-[11px] text-zinc-500 flex items-center gap-1 leading-tight">
          <Icon className="w-3 h-3 text-[var(--brand-accent)]"/>
          <span className="truncate">{contextLabel}</span>
        </div>
      </div>
      <button
        onClick={onAccept}
        data-testid={`huddle-banner-join-${call.id}`}
        className="h-8 px-3 rounded-full bg-[var(--brand-accent)] hover:bg-orange-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition"
      >
        <Icon className="w-3.5 h-3.5"/> Join
      </button>
      <button
        onClick={onDismiss}
        data-testid={`huddle-banner-dismiss-${call.id}`}
        className="p-1 rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4"/>
      </button>
    </div>
  );
}
